<script setup>
/**
 * Sends the customer a shortlist of flights, from the composer toolbar beside Request
 * Payment - the same kind of action, composing something the customer receives.
 *
 * Gated on an enabled GDS connection as well as the feature, because the modal behind it
 * runs a metered search. An account with travel on but nothing connected would get a
 * button that always fails.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SendFlightOptionsModal from 'dashboard/components/widgets/conversation/travel/SendFlightOptionsModal.vue';

const props = defineProps({
  conversationId: {
    type: Number,
    default: 0,
  },
  pinned: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['availability']);

const store = useStore();
const { isCloudFeatureEnabled } = useAccount();
const appIntegrations = useMapGetter('integrations/getAppIntegrations');

const showButton = computed(
  () =>
    isCloudFeatureEnabled(FEATURE_FLAGS.TRAVEL) &&
    appIntegrations.value.some(
      integration =>
        integration.id === 'travelport' && !!integration.hooks?.length
    )
);

onMounted(() => {
  if (!appIntegrations.value.length) store.dispatch('integrations/get');
});

const showModal = ref(false);

const closeModal = () => {
  showModal.value = false;
};

// The composer toolbar lists this tool in its "More tools" menu, so it needs to
// know whether the integration is set up.
watch(showButton, isAvailable => emit('availability', isAvailable), {
  immediate: true,
});

defineExpose({
  trigger: () => {
    showModal.value = true;
  },
});
</script>

<template>
  <NextButton
    v-if="showButton"
    v-show="pinned"
    v-tooltip.top-end="$t('TRAVEL.SEND_OPTIONS.TOOLTIP')"
    icon="i-ph-airplane-tilt"
    slate
    faded
    sm
    @click="showModal = true"
  />
  <SendFlightOptionsModal
    v-if="showModal"
    :conversation-id="props.conversationId"
    @close="closeModal"
  />
</template>
